import { useState } from "react";
import { toast } from "sonner";
import { Coins, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TIP_AMOUNTS = [10, 25, 50, 100, 250];

export function TipDialog({ to, roomId, trigger, onTipped }) {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(25);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/tips", { to_user_id: to.id, tokens: Number(amount), room_id: roomId || null });
      toast.success(`You tipped ${to.name} ${amount} tokens`);
      refresh();
      onTipped?.(amount);
      setOpen(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid="tip-dialog" className="border-zinc-800 bg-zinc-950">
        <DialogHeader><DialogTitle className="font-display text-3xl">Tip {to.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-400">Your balance: <span className="text-amber-300">{user?.tokens || 0} tokens</span></p>
        <div className="flex flex-wrap gap-2">
          {TIP_AMOUNTS.map((a) => (
            <button key={a} data-testid={`tip-amount-${a}`} onClick={() => setAmount(a)}
              className={`rounded-full border px-4 py-2 text-sm transition-colors ${amount === a ? "border-red-600 bg-red-600 text-white" : "border-white/15 hover:border-red-600/60"}`}>{a}</button>
          ))}
        </div>
        <Input data-testid="tip-custom-input" type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-zinc-900" />
        <button data-testid="tip-send-btn" disabled={busy || amount < 1} onClick={send} className="btn-red flex items-center justify-center gap-2 rounded-full py-3 font-semibold disabled:opacity-50">
          <Coins className="h-4 w-4" />Send {amount} tokens
        </button>
      </DialogContent>
    </Dialog>
  );
}

export function SendMoneyDialog({ to, trigger }) {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("50");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/wallet/send", { to_user_id: to.id, amount_cents: Math.round(Number(amount) * 100), note });
      toast.success(`$${Number(amount).toFixed(2)} sent to ${to.name}`);
      refresh();
      setOpen(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid="send-money-dialog" className="border-zinc-800 bg-zinc-950">
        <DialogHeader><DialogTitle className="font-display text-3xl">Send cash to {to.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-400">Wallet balance: ${((user?.wallet_cents || 0) / 100).toFixed(2)} · VIP only</p>
        <div className="flex items-center border-b border-white/15 pb-2">
          <span className="font-display text-5xl text-zinc-500">$</span>
          <input data-testid="send-money-amount" type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)}
            className="font-display w-full bg-transparent text-6xl outline-none" />
        </div>
        <Input data-testid="send-money-note" placeholder="Add a note (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="bg-zinc-900" />
        <button data-testid="send-money-submit" disabled={busy || Number(amount) < 1} onClick={send} className="btn-red flex items-center justify-center gap-2 rounded-full py-3 font-semibold disabled:opacity-50">
          <Send className="h-4 w-4" />Send now
        </button>
      </DialogContent>
    </Dialog>
  );
}
